import React, { useEffect, useRef, useState } from 'react';

import { CustomOperatorValue } from '@aline/common';
import { routes } from '@aline/routes';
import { Badge, Typography } from '@mui/material';
import type { GridApiPremium, GridFilterModel } from '@mui/x-data-grid-premium';
import {
  GridApiContext,
  gridFilterModelSelector,
  GridPreferencePanelsValue,
  gridSidebarOpenSelector,
  GridSidebarValue,
} from '@mui/x-data-grid-premium';
import { isEqual } from 'lodash';
import { CircuitBoardIcon, ListFilter } from 'lucide-react';
import { useMatch } from 'react-router-dom';
import { DropdownSelectButton, PrimaryPlusIconButton, SearchInput } from 'src/components';
import { useAccessGuard } from 'src/components/auth';
import { CreateInsightChartDialog } from 'src/components/insights/CreateInsightChartDialog';
import type { BoardLocalState } from 'src/components/workflow/board/Board';
import { BoardFilterPicker } from 'src/components/workflow/board/board-toolbar/BoardFilterPicker';
import { CreateWorkflowDialog } from 'src/components/workflow/board/CreateWorkflowDialog';
import { BoardFilters } from 'src/components/workflow/board/filters';
import { Apollo } from 'src/gql/generated';
import { useAlineMeasure } from 'src/hooks/dom-hooks';
import type { ReactSetStateTuple } from 'src/types';
import { getIsPlainRecord } from 'src/utils/get-is-plain-record';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

import type { WorkflowListChartsIntegrationSlice } from './workflow-list-charts';
import type { WorkflowListPresetId } from './workflow-views';
import { WorkflowFilters } from './WorkflowFilters';
import { WorkflowGroupingMenu } from './WorkflowGroupingMenu';
import { WorkflowModeSwitcher } from './WorkflowModeSwitcher';
import { WorkflowViewsMenu } from './WorkflowViewsMenu';

export const toolbarHeightPx = 52;
/** Floor for the search field so quick filters keep their space on narrow screens. */
const SEARCH_MIN_WIDTH_PX = 180;
/**
 * Below this toolbar width the mode switcher drops its labels for icons + tooltips.
 * It keys off the measured toolbar rather than off whether the quick filters
 * overflowed: dropping the labels hands width straight back to those filters, so
 * reacting to their overflow would let the two controls flip each other forever.
 */
const MODE_LABEL_MIN_TOOLBAR_WIDTH_PX = 1040;
export const useListToolbarStyles = makeStyles()(theme => ({
  toolbar: {
    alignItems: 'center',
    display: 'flex',
    // Count badges overhang their button by 8px, so the gap has to clear that.
    gap: theme.spacing(1.5),
    maxHeight: toolbarHeightPx,
    minHeight: toolbarHeightPx,
  },
}));

const useStyles = makeStyles()(theme => ({
  filtersButtonBadge: {
    '& .MuiBadge-badge': {
      right: 2,
      top: 2,
    },
  },
  newWorkflowButton: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  rightActions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
    marginLeft: 'auto',
    /**
     * The search field is the only thing here that gives up space when the
     * toolbar gets tight; Display and New Workflow hold their size.
     */
    minWidth: 0,
  },
  title: {
    flexShrink: 0,
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
}));

function getIsBoardFilterValue(value: unknown): value is { id: string } {
  if (!getIsPlainRecord(value)) return false;
  return typeof value.id === 'string';
}

function getSelectedBoardIdsFromFilterModel(filterModel: GridFilterModel): string[] {
  const filterItem = filterModel.items.find(item => item.field === 'board');
  const rawValues = filterItem?.value;
  if (!Array.isArray(rawValues)) return [];
  return rawValues.filter(getIsBoardFilterValue).map(value => value.id);
}

export interface WorkflowsToolbarProps {
  boardStateTuple: ReactSetStateTuple<BoardLocalState>;
  /** Insights-mode search, which filters Chart cards by title rather than Workflows. */
  chartSearchTuple: ReactSetStateTuple<string>;
  chartsIntegrationSlice: WorkflowListChartsIntegrationSlice;
  filtersButtonRef: (el: HTMLElement | null) => void;
  listApiRef: React.RefObject<GridApiPremium | null> | null;
  onActiveWorkflowViewIdChange: (viewId: string | null) => void;
  onBoardChange: (boardId: string) => void;
  onChartsIntegrationSliceChange: (slice: WorkflowListChartsIntegrationSlice) => void;
  onWorkflowListPresetChange: (presetId: WorkflowListPresetId) => void;
  selectedBoardId: string | undefined;
  workflowListPreset: WorkflowListPresetId;
  activeWorkflowViewId?: string | null;
}

export function WorkflowsToolbar({
  activeWorkflowViewId = null,
  boardStateTuple,
  chartSearchTuple,
  chartsIntegrationSlice,
  filtersButtonRef,
  listApiRef,
  onActiveWorkflowViewIdChange,
  onBoardChange,
  onChartsIntegrationSliceChange,
  onWorkflowListPresetChange,
  selectedBoardId,
  workflowListPreset,
}: WorkflowsToolbarProps): JSX.Element {
  const { classes } = useStyles();
  const { classes: toolbarClasses } = useListToolbarStyles();
  const { data: boardsData } = Apollo.useGetBoardsQuery();
  const boards = boardsData?.boards ?? [];
  const [boardState, setBoardState] = boardStateTuple;
  const [chartSearch, setChartSearch] = chartSearchTuple;
  const [toolbarRef, toolbarMeasurements] = useAlineMeasure<HTMLDivElement>();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [chartsOpen, setChartsOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createChartDialogOpen, setCreateChartDialogOpen] = useState(false);
  const handleCreateWorkflow = useAccessGuard({
    callback: () => setCreateDialogOpen(true),
    minRole: Apollo.OrgRole.Basic,
  });
  const isBoard = Boolean(useMatch({ end: false, path: routes.WorkflowBoard }));
  const isInsights = Boolean(useMatch({ end: false, path: routes.WorkflowInsights }));
  const isList = !isBoard && !isInsights;
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const syncedApiRef = useRef<React.RefObject<GridApiPremium | null> | null>(null);
  const debouncedSetQuickFilter = useDebouncedCallback((searchString: string) => {
    if (!listApiRef?.current) return;
    const nextValues = searchString ? searchString.split(' ') : [];
    // Re-applying identical values would clear the data source cache and reset scroll.
    if (isEqual(gridFilterModelSelector(listApiRef).quickFilterValues ?? [], nextValues)) return;
    listApiRef.current.setQuickFilterValues(nextValues);
  }, 500);

  /**
   * The search box is the source of truth, so push it into the grid whenever it
   * changes — including when a fresh grid attaches. Effect setup also re-runs
   * every time the tab is re-shown (React.Activity); the equality check inside
   * the debounced callback keeps that a no-op.
   */
  useEffect(() => {
    if (!isList) return;
    debouncedSetQuickFilter(boardState.searchString);
    return debouncedSetQuickFilter.cancel;
  }, [boardState.searchString, debouncedSetQuickFilter, isList, listApiRef]);

  useEffect(() => {
    const gridApi = listApiRef?.current;
    /**
     * The list detaches its api ref whenever it unmounts, including on tab hide,
     * so leave the last known filter state alone until a grid is back.
     */
    if (!gridApi) return;

    // Only a different grid instance starts with its preferences panel closed.
    if (syncedApiRef.current !== listApiRef) {
      syncedApiRef.current = listApiRef;
      setFiltersOpen(false);
      setChartsOpen(false);
    }
    setFilterModel(gridFilterModelSelector(listApiRef));
    return gridApi.subscribeEvent('filterModelChange', setFilterModel);
  }, [listApiRef]);

  /**
   * The Charts panel can also be closed from its own header, so the button's
   * selected state follows the grid rather than only this component's clicks.
   */
  useEffect(() => {
    const gridApi = listApiRef?.current;
    if (!gridApi) return;

    setChartsOpen(gridSidebarOpenSelector(listApiRef));
    const unsubscribeOpen = gridApi.subscribeEvent('sidebarOpen', ({ value }) => {
      if (value === GridSidebarValue.Charts) setChartsOpen(true);
    });
    const unsubscribeClose = gridApi.subscribeEvent('sidebarClose', ({ value }) => {
      if (value === GridSidebarValue.Charts) setChartsOpen(false);
    });
    return () => {
      unsubscribeOpen();
      unsubscribeClose();
    };
  }, [listApiRef]);

  const toggleFilters = () => {
    if (!listApiRef?.current) return;
    if (filtersOpen) {
      listApiRef.current.hidePreferences();
      setFiltersOpen(false);
      return;
    }
    if (chartsOpen) {
      listApiRef.current.setChartsPanelOpen(false);
    }
    listApiRef.current.showPreferences(GridPreferencePanelsValue.filters);
    setFiltersOpen(true);
  };

  const selectedListBoardIds = getSelectedBoardIdsFromFilterModel(filterModel);
  const boardPickerSelectedIds = isBoard
    ? selectedBoardId
      ? [selectedBoardId]
      : []
    : selectedListBoardIds;

  const handleBoardPickerChange = (boardIds: string[]) => {
    if (isBoard) {
      const boardId = boardIds[0];
      if (boardId) onBoardChange(boardId);
      return;
    }

    if (!listApiRef?.current) return;
    const updatedItems = filterModel.items.filter(item => item.field !== 'board');
    // The Filters panel renders these values as tags, so they need the full board, not just its id.
    const selectedBoards = boards
      .filter(board => boardIds.includes(board.id))
      .map(board => ({
        __typename: 'Board',
        color: board.color,
        icon: board.icon,
        id: board.id,
        name: board.name,
      }));
    if (selectedBoards.length > 0) {
      updatedItems.push({
        field: 'board',
        operator: CustomOperatorValue.WorkflowBoard,
        value: selectedBoards,
      });
    }
    listApiRef.current.setFilterModel({ ...filterModel, items: updatedItems });
  };

  // In board view a board is always selected, so only the list view's board filter gets a badge.
  const boardFilterCount = isBoard ? 0 : boardPickerSelectedIds.length;
  const filterCount = filterModel.items.length;
  const isModeSwitcherCompact = Boolean(
    toolbarMeasurements && toolbarMeasurements.width < MODE_LABEL_MIN_TOOLBAR_WIDTH_PX
  );

  return (
    <div ref={toolbarRef} className={toolbarClasses.toolbar}>
      <Typography className={classes.title} variant="h6">
        Workflows
      </Typography>
      {isList && (
        <Badge badgeContent={filterCount} className={classes.filtersButtonBadge} color="primary">
          <DropdownSelectButton
            ref={node => {
              filtersButtonRef(node);
            }}
            buttonVariant="default"
            icon={<ListFilter size={18} />}
            label="Filters"
            selected={filtersOpen}
            variant="outlined"
            onClick={toggleFilters}
          />
        </Badge>
      )}
      {isList && (
        <WorkflowViewsMenu
          activeWorkflowViewId={activeWorkflowViewId}
          chartsIntegrationSlice={chartsIntegrationSlice}
          listApiRef={listApiRef}
          workflowListPreset={workflowListPreset}
          onActiveWorkflowViewIdChange={onActiveWorkflowViewIdChange}
          onChartsIntegrationSliceChange={onChartsIntegrationSliceChange}
          onWorkflowListPresetChange={onWorkflowListPresetChange}
        />
      )}
      {!isInsights && (
        <>
          <BoardFilterPicker
            boards={boards}
            mode={isBoard ? 'single' : 'multi'}
            renderTrigger={(currentBoard, onClick, isOpen) => (
              <Badge
                badgeContent={boardFilterCount}
                className={classes.filtersButtonBadge}
                color="primary"
              >
                <DropdownSelectButton
                  icon={<CircuitBoardIcon height={16} width={16} />}
                  label={isBoard ? (currentBoard?.name ?? 'Board') : 'Board'}
                  selected={isOpen}
                  variant="outlined"
                  onClick={onClick}
                />
              </Badge>
            )}
            selectedBoardIds={boardPickerSelectedIds}
            onChange={handleBoardPickerChange}
          />
          {!isBoard && <WorkflowGroupingMenu listApiRef={listApiRef} />}
          <WorkflowFilterControls
            boardStateTuple={boardStateTuple}
            isBoard={isBoard}
            listApiRef={listApiRef}
          />
        </>
      )}
      <div className={classes.rightActions}>
        <WorkflowModeSwitcher compact={isModeSwitcherCompact} />
        <SearchInput
          minWidth={SEARCH_MIN_WIDTH_PX}
          placeholder={isInsights ? 'Search charts...' : 'Search workflows...'}
          search={isInsights ? chartSearch : boardState.searchString}
          setSearch={
            isInsights
              ? setChartSearch
              : searchString => setBoardState(s => ({ ...s, searchString }))
          }
        />
        <PrimaryPlusIconButton
          className={classes.newWorkflowButton}
          tooltip={isInsights ? 'Add chart' : 'New Workflow'}
          onClick={isInsights ? () => setCreateChartDialogOpen(true) : handleCreateWorkflow}
        />
      </div>
      <CreateWorkflowDialog
        initialBoardId={selectedBoardId}
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
      <CreateInsightChartDialog
        open={createChartDialogOpen}
        onClose={() => setCreateChartDialogOpen(false)}
      />
    </div>
  );
}

function WorkflowFilterControls({
  boardStateTuple,
  isBoard,
  listApiRef,
}: {
  boardStateTuple: ReactSetStateTuple<BoardLocalState>;
  isBoard: boolean;
  listApiRef: React.RefObject<GridApiPremium | null> | null;
}): JSX.Element | null {
  if (isBoard) return <BoardFilters boardStateTuple={boardStateTuple} />;

  return <ListFilterChips listApiRef={listApiRef} />;
}

interface ListFilterChipsProps {
  listApiRef: React.RefObject<GridApiPremium | null> | null;
}

function ListFilterChips({ listApiRef }: ListFilterChipsProps): JSX.Element | null {
  if (!listApiRef) return null;

  return (
    <GridApiContext.Provider value={listApiRef}>
      <WorkflowFilters />
    </GridApiContext.Provider>
  );
}
